import { useEffect } from 'react';
import { Box, Typography, Paper, Stack, IconButton, Chip } from '@mui/material';
import { Bell, Check, Users, FileText, Calendar } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import useTeacherStore from '@/store/teacherStore';
import { Loader } from '@/components/feedback/Loader';
import teacherService from '@/services/teacherService';

export function TeacherNotificationsPage() {
  const { user } = useAuth();
  const fetchTeacherNotifications = useTeacherStore(s => s.fetchTeacherNotifications);
  const notifications = useTeacherStore(s => s.notifications);
  const loading = useTeacherStore(s => s.loading);

  useEffect(() => {
    if (user?.teacherId) {
      fetchTeacherNotifications(user.teacherId);
    }
  }, [user?.teacherId, fetchTeacherNotifications]);

  const handleMarkRead = async (id) => {
    await teacherService.markNotificationRead(id);
    fetchTeacherNotifications(user?.teacherId);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'meeting': return <Users size={20} />;
      case 'circular': return <FileText size={20} />;
      case 'holiday': return <Calendar size={20} />;
      default: return <Bell size={20} />;
    }
  };

  const getColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      default: return 'info';
    }
  };

  if (loading && notifications.length === 0) return <Loader />;

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Notifications
        </Typography>
        <Typography color="text.secondary">
          Updates, circulars, and messages from administration
        </Typography>
      </Box>

      {notifications.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Typography color="text.secondary">No notifications found.</Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {notifications.map(notif => (
            <Paper 
              key={notif.id} 
              sx={{ 
                p: 3, 
                borderRadius: 2, 
                display: 'flex', 
                gap: 3,
                bgcolor: notif.read ? 'background.paper' : 'action.hover'
              }}
            >
              <Box 
                sx={{ 
                  p: 1.5, 
                  borderRadius: '50%', 
                  bgcolor: `${getColor(notif.priority)}.light`, 
                  color: `${getColor(notif.priority)}.main`,
                  height: 'fit-content'
                }}
              >
                {getIcon(notif.type)}
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2 }}>
                  <Typography variant="h6" fontWeight={notif.read ? 500 : 700} sx={{ wordBreak: 'break-word' }}>
                    {notif.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(notif.date).toLocaleString()}
                  </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" mb={2}>
                  {notif.message}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip label={notif.type.toUpperCase()} size="small" variant="outlined" />
                  {!notif.read && (
                    <Chip 
                      label="New" 
                      size="small" 
                      color="primary" 
                    />
                  )}
                </Stack>
              </Box>
              {!notif.read && (
                <IconButton 
                  color="primary" 
                  onClick={() => handleMarkRead(notif.id)}
                  title="Mark as read"
                >
                  <Check />
                </IconButton>
              )}
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
}
